/**
 * Pure helpers for rendering custom-agent result documents against a JSON
 * Schema. Keeps schema walking out of React so it can be unit-tested.
 */

import { formatEventWindowUTC, formatRelativeTime } from '../../lib/time'

export type AxilonDisplayHint = 'kpi' | 'table' | 'markdown' | 'json'

export type EnumTone = 'healthy' | 'watch' | 'elevated' | 'critical' | 'neutral'

export interface SchemaProperty {
  key: string
  /** Human label — schema `title` or a humanized property key. */
  title: string
  type?: string
  display?: AxilonDisplayHint
  /** Top-level `x-reviewable` only. Absent when the property is unannotated. */
  review?: boolean
  schema: Record<string, unknown>
}

export interface FormatScalarOptions {
  /** Reference "now" for `format: date-time` relative labels. */
  now?: number
}

const PRIMARY_FIELD_KEYS = ['headline', 'title', 'summary', 'name'] as const

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

function primaryType(schema: Record<string, unknown>): string | undefined {
  const t = schema.type
  if (typeof t === 'string') return t
  if (Array.isArray(t)) {
    const nonNull = t.find((x): x is string => typeof x === 'string' && x !== 'null')
    return nonNull
  }
  return undefined
}

function inferType(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') return 'object'
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number'
  if (typeof value === 'boolean') return 'boolean'
  return 'string'
}

/**
 * Sentence-case a machine key. Schema `title` still wins at parse time;
 * this is the fallback for undeclared keys and untitled properties.
 */
export function humanizeKey(key: string): string {
  const trimmed = key.trim()
  if (!trimmed) return key
  if (/\s/.test(trimmed)) return trimmed
  const spaced = trimmed
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!spaced) return trimmed
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** True when the schema documents a closed unit interval (UI shows a percent). */
export function isUnitIntervalSchema(schema: Record<string, unknown> | undefined): boolean {
  return schema?.minimum === 0 && schema?.maximum === 1
}

export function schemaEnumValues(
  schema: Record<string, unknown> | undefined
): string[] | undefined {
  if (!schema || !Array.isArray(schema.enum)) return undefined
  const values = schema.enum.filter((v): v is string => typeof v === 'string')
  return values.length > 0 ? values : undefined
}

/**
 * Map a known priority / verdict enum onto the shared severity bands.
 * Unknown vocabularies stay `neutral`.
 */
export function enumTone(value: string): EnumTone {
  switch (value.toLowerCase()) {
    case 'critical':
    case 'degraded':
      return 'critical'
    case 'high':
      return 'elevated'
    case 'medium':
    case 'watch':
      return 'watch'
    case 'low':
    case 'ok':
      return 'healthy'
    default:
      return 'neutral'
  }
}

export function siblingUnit(record: Record<string, unknown>): string | undefined {
  const unit = record.unit
  return typeof unit === 'string' && unit.trim() ? unit.trim() : undefined
}

/**
 * Impact-shaped objects are `{ value, unit, context? }`. Only the `value`
 * field consumes the sibling unit — other numbers in the same record keep
 * their own meaning.
 */
export function attachedUnit(record: Record<string, unknown>, key: string): string | undefined {
  if (key !== 'value' || typeof record.value !== 'number') return undefined
  return siblingUnit(record)
}

/** Hide a `unit` row once the `value` field has consumed it. */
export function shouldOmitUnitKey(record: Record<string, unknown>, key: string): boolean {
  if (key !== 'unit') return false
  return typeof record.value === 'number' && siblingUnit(record) !== undefined
}

/**
 * Lift identifying fields to the front when the schema does not declare
 * property order. Candidate order among themselves is fixed; everything
 * else keeps its original order. Missing candidates leave the list unchanged.
 */
export function orderPrimaryKeys(keys: string[]): string[] {
  const primary = PRIMARY_FIELD_KEYS.filter((k) => keys.includes(k))
  const rest = keys.filter(
    (k) => !PRIMARY_FIELD_KEYS.includes(k as (typeof PRIMARY_FIELD_KEYS)[number])
  )
  return [...primary, ...rest]
}

/** Every own value is a scalar (or the object is empty — caller decides). */
export function isAllScalarObject(value: unknown): boolean {
  const record = asRecord(value)
  if (!record) return false
  const values = Object.values(record)
  if (values.length === 0) return false
  return values.every(isScalar)
}

export function getDisplayHint(schema: Record<string, unknown>): AxilonDisplayHint | undefined {
  const raw = schema['x-axilon-display']
  if (raw === 'kpi' || raw === 'table' || raw === 'markdown' || raw === 'json') {
    return raw
  }
  return undefined
}

export function getReviewAnnotation(schema: Record<string, unknown>): boolean | undefined {
  const raw = schema['x-reviewable']
  return typeof raw === 'boolean' ? raw : undefined
}

/** True when any top-level property carries `x-reviewable`. Nested marks do not count. */
export function schemaHasTopLevelReviewAnnotation(schema: unknown): boolean {
  return parseObjectSchema(schema).some((p) => p.review !== undefined)
}

/**
 * Whether a shredded result type should show review chrome.
 * Hide only when this property is marked `x-reviewable: false`.
 * Unannotated and unknown types are reviewable.
 */
export function isReviewableResultType(outputSchema: unknown, type: string): boolean {
  const prop = parseObjectSchema(outputSchema).find((p) => p.key === type)
  return prop?.review !== false
}

/** Top-level properties of a root object schema (empty if not an object schema). */
export function parseObjectSchema(schema: unknown): SchemaProperty[] {
  const root = asRecord(schema)
  if (!root) return []
  const props = asRecord(root.properties)
  if (!props) return []
  return Object.entries(props).map(([key, raw]) => {
    const propSchema = asRecord(raw) ?? {}
    const titled = typeof propSchema.title === 'string' ? propSchema.title.trim() : ''
    const review = getReviewAnnotation(propSchema)
    return {
      key,
      title: titled && titled !== key ? titled : humanizeKey(key),
      type: primaryType(propSchema),
      display: getDisplayHint(propSchema),
      ...(review !== undefined ? { review } : {}),
      schema: propSchema
    }
  })
}

export function inferObjectProperties(record: Record<string, unknown>): SchemaProperty[] {
  return orderPrimaryKeys(Object.keys(record)).map((key) => ({
    key,
    title: humanizeKey(key),
    type: inferType(record[key]),
    schema: {}
  }))
}

/**
 * Schema property order wins when the schema declares `properties`.
 * Otherwise infer from the value and apply the primary-field heuristic.
 * Undeclared extra keys are appended with humanized labels.
 */
export function objectPropertiesForValue(
  record: Record<string, unknown>,
  schema: unknown
): SchemaProperty[] {
  const declared = parseObjectSchema(schema)
  if (declared.length === 0) {
    return inferObjectProperties(record)
  }
  const seen = new Set(declared.map((p) => p.key))
  const extras = Object.keys(record)
    .filter((k) => !seen.has(k))
    .map((key) => ({
      key,
      title: humanizeKey(key),
      type: inferType(record[key]),
      schema: {}
    }))
  return [...declared, ...extras]
}

export function isScalar(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

function formatDateTime(value: string | number, now?: number): string | undefined {
  const utc = formatEventWindowUTC(value)
  if (!utc) return undefined
  const rel = formatRelativeTime(value, { now, fallback: '' })
  return rel ? `${utc} (${rel})` : utc
}

/** Compact cell/list formatting for scalars and short arrays of scalars. */
export function formatScalar(
  value: unknown,
  schema?: Record<string, unknown>,
  options: FormatScalarOptions = {}
): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (schema?.format === 'date-time') {
      return formatDateTime(value, options.now) ?? String(value)
    }
    if (!Number.isFinite(value)) return String(value)
    if (isUnitIntervalSchema(schema) && value >= 0 && value <= 1) {
      return `${Math.round(value * 100)}%`
    }
    return Number.isInteger(value)
      ? String(value)
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0) return '—'
    if (schema?.format === 'date-time') {
      return formatDateTime(trimmed, options.now) ?? trimmed
    }
    return trimmed
  }
  if (Array.isArray(value) && value.every(isScalar)) {
    if (value.length === 0) return '—'
    return value.map((v) => formatScalar(v)).join(', ')
  }
  return '…'
}

/**
 * Pick up to `max` top-level scalar (or scalar-array) properties for the
 * results list columns. Prefer properties marked `x-axilon-display: "kpi"`.
 */
export function pickListColumns(schema: unknown, max = 3): SchemaProperty[] {
  const props = parseObjectSchema(schema)
  if (props.length === 0 || max <= 0) return []

  const isListFriendly = (p: SchemaProperty): boolean => {
    if (p.display === 'json' || p.display === 'table') return false
    if (p.type === 'object') return false
    if (p.type === 'array') {
      const items = asRecord(p.schema.items)
      const itemType = items ? primaryType(items) : undefined
      return (
        itemType === 'string' ||
        itemType === 'number' ||
        itemType === 'integer' ||
        itemType === 'boolean'
      )
    }
    return true
  }

  const kpis = props.filter((p) => p.display === 'kpi' && isListFriendly(p))
  const rest = props.filter((p) => p.display !== 'kpi' && isListFriendly(p))
  return [...kpis, ...rest].slice(0, max)
}

export function readResultField(result: Record<string, unknown>, key: string): unknown {
  return result[key]
}

/** True when `value` looks like an array of plain objects (table rows). */
export function isObjectArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.length > 0 && value.every((row) => asRecord(row) !== null)
}

export function itemObjectProperties(arraySchema: Record<string, unknown>): SchemaProperty[] {
  const items = asRecord(arraySchema.items)
  if (!items) return []
  return parseObjectSchema(items)
}
